import { cn } from '@invent/wl-ui-kit-next';
import { Button, Checkbox, Input } from '@invent/wl-ui-kit-next/ui';
import { Paperclip, Send, X } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import {
  ChoiceSelect,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  HelpPopover
} from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { allowedDomainsText, useMailDelivery } from '@/domains/auth';
import type { IListQuery, IListRow } from '../../api/lists-contracts';
import { mailCampaignsApi } from '../../api/mail-campaigns-api';
import type { ICampaignPreview, IMailCampaign } from '../../api/mail-campaign-contracts';
import type { MailSurvey } from '../../api/mail-template-contracts';
import { mailTemplatesApi } from '../../api/mail-templates-api';
import { useAdminData } from '../../components/admin-page';
import { mailLogPath } from '../../paths';
import { ExcludedLine } from '../mail-log/excluded-line';
import { fieldRefusal, refusalMessage } from '../../refusal';
import { PREVIEW_DEFAULTS, PreviewBody, type IPreviewState } from '../mail-templates/mail-preview';
import { TagChips, TagsHelp } from '../mail-templates/template-tags';
import { nameOf } from './editing';

/**
 * Lists' *Email the selected rows…* and *Email every row of this list…* (SR-523 stage 3): an
 * template used from Lists sent as one campaign, in three steps. **Compose** — the template (the
 * list's survey picks its variant) with its tags, read-only, the campaign's name — the send's own
 * label — fixed CC
 * addresses, whether replies come back to the record, and on a test environment the inbox every
 * mail goes to instead. **Check** — survey-api makes the campaign a draft from a snapshot of the
 * recipients, and the step shows the first three recipients' mails as the worker renders them,
 * who is reached and who is skipped, the attachments, and a test to the caller's own address.
 * **Send** — a last word, then the launch: survey-api pins the template's version and queues the
 * mails, which go out within the bulk limits; the campaign's page in the Email log follows them.
 *
 * A draft left by Cancel is deleted; one the browser lost stays in the Email log's Campaigns, where
 * it can be deleted too.
 */

/** survey-api's `Mail:Campaign:MaxRecipients` default: past it, the selection is narrowed. */
export const CAMPAIGN_MAX_RECIPIENTS = 5000;

const ADDRESS = /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/;

const MAX_CC = 5;

type Step = 'compose' | 'check' | 'send' | 'launched';

export function SendEmailDialog({
  survey,
  rows,
  query,
  total,
  onClose
}: {
  survey: string;
  /** The ticked rows — or null: every row of the list's query. */
  rows: Nullable<readonly IListRow[]>;
  query: IListQuery;
  total: number;
  onClose: () => void;
}) {
  const id = React.useId();
  const delivery = useMailDelivery();
  const templates = useAdminData(
    async (signal) =>
      (await mailTemplatesApi.list(signal, { usage: 'lists' })).filter(
        (template) => template.usages.includes('lists') && !template.archivedAt
      ),
    []
  );
  const [step, setStep] = React.useState<Step>('compose');
  const [template, setTemplate] = React.useState('');
  const [name, setName] = React.useState('');
  const [cc, setCc] = React.useState<string[]>([]);
  const [ccDraft, setCcDraft] = React.useState('');
  const [replyRouting, setReplyRouting] = React.useState(true);
  const [deliverTo, setDeliverTo] = React.useState('');
  const [campaign, setCampaign] = React.useState<Nullable<IMailCampaign>>(null);
  const [busy, setBusy] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  // Bulk mail is off on this environment: said by the campaign, or by a launch that was refused.
  const [launchRefusedOff, setLaunchRefusedOff] = React.useState(false);
  const bulkOff = campaign?.bulkEnabled === false || launchRefusedOff;
  const count = rows ? rows.length : total;
  const chosen = templates.data?.find((entry) => entry.code === template) ?? null;

  // The first template and a name, once the templates are read.
  React.useEffect(() => {
    const first = templates.data?.[0];
    if (!first || template) return;
    setTemplate(first.code);
    setName(defaultName(first.name));
  }, [templates.data, template]);

  const ccProblem = ccDraft.trim() && !ADDRESS.test(ccDraft.trim()) ? 'One plain address, like name@firm.com.' : null;
  const addCc = () => {
    const next = ccDraft.trim().toLowerCase();
    if (!next || !ADDRESS.test(next) || cc.includes(next) || cc.length >= MAX_CC) return;
    setCc([...cc, next]);
    setCcDraft('');
  };
  const deliverProblem = deliverTo.trim() && !ADDRESS.test(deliverTo.trim()) ? 'One plain address.' : null;
  // A test environment mails only its allowed domains; a test inbox takes every mail instead.
  const redirect = delivery?.redirect === true;
  const composeBlocked = busy || !chosen || name.trim() === '' || Boolean(ccProblem) || Boolean(deliverProblem);

  const close = () => {
    // A draft nobody launched is not left behind.
    if (campaign && campaign.status === 'draft' && step !== 'launched') void mailCampaignsApi.remove(campaign.id).catch(() => {});
    onClose();
  };

  const compose = async () => {
    if (composeBlocked || !chosen) return;
    setBusy(true);
    setRefusal(null);
    const inbox = redirect && deliverTo.trim() ? deliverTo.trim() : null;
    // A draft's PATCH takes the name, CC and reply routing; another template or test inbox is a new draft.
    const sameInbox = (campaign?.deliverTo ?? null)?.toLowerCase() === inbox?.toLowerCase();
    try {
      if (campaign && campaign.template === chosen.code && sameInbox) {
        setCampaign(
          await mailCampaignsApi.patch(campaign.id, campaign.revision, { name: name.trim(), cc, replyRouting })
        );
      } else {
        if (campaign) await mailCampaignsApi.remove(campaign.id).catch(() => {});
        setCampaign(
          await mailCampaignsApi.create({
            template: chosen.code,
            survey: survey as MailSurvey,
            name: name.trim(),
            participants: rows ? rows.map((row) => ({ participantId: row.participantId, survey: row.survey })) : null,
            query: rows ? null : query,
            cc,
            replyRouting,
            deliverTo: inbox
          })
        );
      }
      setStep('check');
    } catch (error) {
      setRefusal(
        fieldRefusal(error, 'participants') ??
          fieldRefusal(error, 'deliverTo') ??
          fieldRefusal(error, 'template') ??
          refusalMessage(error, 'The campaign could not be prepared.')
      );
    } finally {
      setBusy(false);
    }
  };

  const launch = async () => {
    if (!campaign) return;
    setBusy(true);
    setRefusal(null);
    try {
      await mailCampaignsApi.launch(campaign.id, campaign.revision);
      setStep('launched');
    } catch (error) {
      if (error instanceof ApiError && error.isProblem(ProblemType.bulkMailOff)) {
        setLaunchRefusedOff(true);
        return;
      }
      setRefusal(
        error instanceof ApiError && error.status === 409
          ? 'The campaign changed or was sent meanwhile — open it in the Email log.'
          : refusalMessage(error, 'The campaign could not be sent.')
      );
    } finally {
      setBusy(false);
    }
  };

  const titles: Record<Step, string> = {
    compose: rows ? `Email ${countWords(count, 'selected row')}` : `Email every row of this list`,
    check: 'Check the emails',
    send: 'Send',
    launched: 'Sending'
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && close()}>
      <DialogContent className={cn(step === 'check' ? 'max-w-5xl' : 'max-w-xl')} data-send-email={step}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1">
            {titles[step]}
            <HelpPopover label="About sending an email" className="size-6">
              <p>
                One campaign: the template&apos;s current version for this survey, filled for each
                recipient. It goes to the address their record names; anyone who unsubscribed or has
                no address is skipped.
              </p>
              <p>
                Each email sent is recorded on the record&apos;s activity as an email event. Mails go
                out within the daily sending limits, so a large campaign can take hours.
              </p>
            </HelpPopover>
          </DialogTitle>
          <DialogDescription>
            <StepLine step={step} />
          </DialogDescription>
        </DialogHeader>

        {step === 'compose' && (
          <DialogBody
            as="form"
            id={`${id}-compose`}
            onSubmit={(event) => {
              event.preventDefault();
              void compose();
            }}
            className="text-[0.8125rem]"
          >
            {templates.error && <p className="text-destructive">{templates.error}</p>}
            {templates.data && templates.data.length === 0 && (
              <p className="text-muted-foreground">No template is offered for Lists yet — create one in Setup → Email templates.</p>
            )}
            <div className="grid gap-1">
              <span id={`${id}-template`} className="font-medium">
                Template
              </span>
              <ChoiceSelect
                labelledBy={`${id}-template`}
                value={template}
                disabled={!templates.data?.length}
                onChange={(code) => {
                  const next = templates.data?.find((entry) => entry.code === code);
                  setTemplate(code);
                  if (next) setName(defaultName(next.name));
                }}
                choices={(templates.data ?? []).map((entry) => ({ value: entry.code, label: entry.name }))}
                className="h-8"
              />
              {chosen && chosen.tags.length > 0 && (
                <span className="flex flex-wrap items-center gap-1.5" data-send-tags="">
                  <span className="text-xs text-muted-foreground">Tags</span>
                  <TagChips tags={chosen.tags} />
                  <TagsHelp />
                </span>
              )}
            </div>
            <div className="grid gap-1">
              <span className="flex items-center gap-1">
                <label htmlFor={`${id}-name`} className="font-medium">
                  Campaign name
                </label>
                <HelpPopover label="About the campaign name" className="size-6">
                  <p>
                    This send&apos;s own label in the Email log. Tags belong to the template: change
                    them in Setup → Email templates.
                  </p>
                </HelpPopover>
              </span>
              <Input
                id={`${id}-name`}
                value={name}
                maxLength={120}
                onChange={(event) => setName(event.target.value)}
                className="h-8"
              />
            </div>
            <div className="grid gap-1">
              <label htmlFor={`${id}-cc`} className="font-medium">
                CC on every email <span className="font-normal text-muted-foreground">(up to {MAX_CC})</span>
              </label>
              <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-input px-2 py-1">
                {cc.map((address) => (
                  <span
                    key={address}
                    className="inline-flex items-center gap-1 rounded-full bg-[var(--color-slight-blue)] py-0.5 pr-1 pl-2 text-xs"
                    data-cc={address}
                  >
                    {address}
                    <button
                      type="button"
                      aria-label={`Remove ${address}`}
                      onClick={() => setCc(cc.filter((each) => each !== address))}
                      className="rounded-full p-0.5 hover:bg-white"
                    >
                      <X className="size-3" aria-hidden />
                    </button>
                  </span>
                ))}
                {cc.length < MAX_CC && (
                  <input
                    id={`${id}-cc`}
                    value={ccDraft}
                    placeholder={cc.length ? '' : 'name@firm.com, then Enter'}
                    aria-invalid={ccProblem ? true : undefined}
                    onChange={(event) => setCcDraft(event.target.value)}
                    onBlur={addCc}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
                        event.preventDefault();
                        addCc();
                      } else if (event.key === 'Backspace' && !ccDraft && cc.length) {
                        setCc(cc.slice(0, -1));
                      }
                    }}
                    className="h-6 min-w-40 flex-1 bg-transparent text-[0.8125rem] outline-none"
                  />
                )}
              </div>
              {ccProblem ? (
                <p className="text-xs text-destructive">{ccProblem}</p>
              ) : (
                <p className="text-xs text-muted-foreground">A record&apos;s own CC address is copied as well.</p>
              )}
            </div>
            <label className="flex items-start gap-2">
              <Checkbox
                checked={replyRouting}
                onCheckedChange={(checked) => setReplyRouting(checked === true)}
                className="mt-0.5"
              />
              <span>
                Replies come back to the record
                <span className="block text-xs text-muted-foreground">
                  Otherwise they go to the template&apos;s reply address.
                </span>
              </span>
            </label>
            {redirect && (
              <div className="grid gap-1">
                <label htmlFor={`${id}-inbox`} className="font-medium">
                  Test inbox <span className="font-normal text-muted-foreground">(optional)</span>
                </label>
                <Input
                  id={`${id}-inbox`}
                  value={deliverTo}
                  placeholder="you@invent.us"
                  aria-invalid={deliverProblem ? true : undefined}
                  onChange={(event) => setDeliverTo(event.target.value)}
                  className="h-8"
                />
                <p className={cn('text-xs', deliverProblem ? 'text-destructive' : 'text-muted-foreground')}>
                  {deliverProblem ??
                    (deliverTo.trim()
                      ? 'Every email of the campaign goes here, without CC.'
                      : `This environment mails ${allowedDomainsText(delivery?.allowedDomains ?? [])} only; other recipients are withheld.`)}
                </p>
              </div>
            )}
            {!rows && total > CAMPAIGN_MAX_RECIPIENTS && (
              <p className="text-destructive">
                {total.toLocaleString('en-US')} rows — a campaign reaches at most{' '}
                {CAMPAIGN_MAX_RECIPIENTS.toLocaleString('en-US')}. Narrow the list or tick the rows.
              </p>
            )}
            {refusal && (
              <p role="alert" className="text-destructive">
                {refusal}
              </p>
            )}
          </DialogBody>
        )}

        {step === 'check' && campaign && (
          <CheckStep campaign={campaign} rows={rows} onCampaign={setCampaign} />
        )}

        {step === 'send' && campaign && (
          <DialogBody className="text-[0.8125rem]">
            <p className="text-sm" data-send-summary="">
              Send <strong>{countWords(campaign.recipients.reachable, 'email')}</strong> of{' '}
              <strong>{chosen?.name ?? campaign.templateName}</strong> (version {campaign.version}) now?
            </p>
            <Skipped campaign={campaign} />
            {campaign.deliverTo && (
              <p className="text-muted-foreground">Every one goes to {campaign.deliverTo}, without CC.</p>
            )}
            <p className="text-muted-foreground">Once sent, it cannot be called back; mails not yet out can be cancelled.</p>
            {bulkOff && (
              <p role="status" className="font-medium text-destructive" data-bulk-off="">
                Bulk mail is off on this environment — the campaign stays a draft.
              </p>
            )}
            {refusal && (
              <p role="alert" className="text-destructive">
                {refusal}
              </p>
            )}
          </DialogBody>
        )}

        {step === 'launched' && campaign && (
          <DialogBody className="text-[0.8125rem]">
            <p role="status" className="text-sm" data-campaign-launched={campaign.id}>
              {countWords(campaign.recipients.reachable, 'email')} queued. They go out within the sending limits.
            </p>
            <Link to={mailLogPath({ tab: 'campaigns', campaign: campaign.id })} className="font-medium text-[var(--color-sapphire)] underline">
              Follow the campaign in the Email log
            </Link>
          </DialogBody>
        )}

        <DialogFooter>
          {step === 'launched' ? (
            <Button type="button" size="sm" onClick={onClose}>
              Done
            </Button>
          ) : (
            <>
              <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={close}>
                Cancel
              </Button>
              {step !== 'compose' && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => setStep(step === 'send' ? 'check' : 'compose')}
                >
                  Back
                </Button>
              )}
              {step === 'compose' && (
                <Button type="submit" form={`${id}-compose`} size="sm" disabled={composeBlocked}>
                  {busy ? 'Preparing…' : 'Next: check the emails'}
                </Button>
              )}
              {step === 'check' && (
                <Button
                  type="button"
                  size="sm"
                  disabled={!campaign || campaign.recipients.reachable === 0}
                  onClick={() => setStep('send')}
                >
                  Next
                </Button>
              )}
              {step === 'send' && (
                <Button
                  type="button"
                  size="sm"
                  disabled={busy || bulkOff}
                  onClick={() => void launch()}
                  data-launch=""
                >
                  <Send className="size-4" aria-hidden />
                  {busy ? 'Sending…' : `Send ${countWords(campaign?.recipients.reachable ?? 0, 'email')}`}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepLine({ step }: { step: Step }) {
  const steps: [Step, string][] = [
    ['compose', 'Compose'],
    ['check', 'Check'],
    ['send', 'Send']
  ];
  const at = step === 'launched' ? 3 : steps.findIndex(([key]) => key === step);
  return (
    <span className="flex items-center gap-2 text-xs">
      {steps.map(([key, words], index) => (
        <span key={key} className={cn(index === at ? 'font-semibold text-[var(--color-royal-blue)]' : 'text-muted-foreground')}>
          {index + 1}. {words}
        </span>
      ))}
    </span>
  );
}

function Skipped({ campaign }: { campaign: IMailCampaign }) {
  const { suppressed, noAddress } = campaign.recipients;
  if (suppressed === 0 && noAddress === 0) return null;
  const parts = [
    suppressed ? `${countWords(suppressed, 'recipient')} unsubscribed` : null,
    noAddress ? `${countWords(noAddress, 'recipient')} without an address` : null
  ].filter(Boolean);
  return <p className="text-muted-foreground">Skipped: {parts.join(', ')}.</p>;
}

/** The second step: the first recipients' mails, the counts, the attachments, a test. */
function CheckStep({
  campaign,
  rows,
  onCampaign
}: {
  campaign: IMailCampaign;
  rows: Nullable<readonly IListRow[]>;
  onCampaign: (campaign: IMailCampaign) => void;
}) {
  const [shown, setShown] = React.useState(campaign.sample[0] ?? null);
  const [preview, setPreview] = React.useState<IPreviewState>({ shown: null, problems: {}, other: null, pending: true });
  const [address, setAddress] = React.useState<Nullable<ICampaignPreview>>(null);
  const [test, setTest] = React.useState<Nullable<{ sent: boolean; text: string }>>(null);
  const [attaching, setAttaching] = React.useState(false);
  const [attachRefusal, setAttachRefusal] = React.useState<Nullable<string>>(null);
  const file = React.useRef<HTMLInputElement>(null);
  const tabsId = React.useId();

  React.useEffect(() => {
    if (!shown) return undefined;
    const controller = new AbortController();
    setPreview((now) => ({ ...now, pending: true }));
    mailCampaignsApi.preview(campaign.id, shown, controller.signal).then(
      (answer) => {
        if (controller.signal.aborted) return;
        setPreview({ shown: answer, problems: {}, other: null, pending: false });
        setAddress(answer);
      },
      (error: unknown) => {
        if (!controller.signal.aborted) {
          setPreview({ shown: null, problems: {}, other: refusalMessage(error, 'The preview could not be rendered.'), pending: false });
        }
      }
    );
    return () => controller.abort();
  }, [campaign.id, shown]);

  const who = (participantId: string, index: number) => {
    const row = rows?.find((each) => each.participantId === participantId);
    return row ? nameOf(row) : `Recipient ${index + 1}`;
  };

  const sendTest = async () => {
    setTest(null);
    try {
      const sent = await mailCampaignsApi.test(campaign.id);
      setTest({ sent: true, text: `Test sent to ${sent.to}.` });
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        const after = Number(error.problem?.retryAfter);
        setTest({
          sent: false,
          text: Number.isFinite(after) && after > 0 ? `Too many tests — try again in ${Math.ceil(after / 60)} min.` : 'Too many tests — try again later.'
        });
      } else {
        setTest({ sent: false, text: refusalMessage(error, 'The test could not be sent.') });
      }
    }
  };

  const attach = async (chosen: File) => {
    setAttaching(true);
    setAttachRefusal(null);
    try {
      await mailCampaignsApi.attach(campaign.id, chosen);
      onCampaign(await mailCampaignsApi.get(campaign.id));
    } catch (error) {
      setAttachRefusal(fieldRefusal(error, 'file') ?? refusalMessage(error, `${chosen.name} could not be attached.`));
    } finally {
      setAttaching(false);
      if (file.current) file.current.value = '';
    }
  };

  const detach = async (attachmentId: string) => {
    setAttachRefusal(null);
    try {
      await mailCampaignsApi.detach(campaign.id, attachmentId);
      onCampaign(await mailCampaignsApi.get(campaign.id));
    } catch (error) {
      setAttachRefusal(refusalMessage(error, 'The file could not be removed.'));
    }
  };

  const { total, reachable, suppressed, noAddress, excluded } = campaign.recipients;
  return (
    <DialogBody className="text-[0.8125rem]">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_17rem]">
        <div className="grid min-w-0 gap-2">
          <div role="tablist" aria-label="Recipient" className="flex flex-wrap gap-1">
            {campaign.sample.map((participantId, index) => (
              <button
                key={participantId}
                type="button"
                role="tab"
                id={`${tabsId}-${index}`}
                aria-selected={participantId === shown}
                onClick={() => setShown(participantId)}
                className={cn(
                  'rounded-md border px-2.5 py-1 text-xs',
                  participantId === shown
                    ? 'border-[var(--color-teal)] bg-[color-mix(in_srgb,var(--color-turquoise)_14%,white)] font-medium text-[var(--color-royal-blue)]'
                    : 'border-border text-muted-foreground hover:text-[var(--color-royal-blue)]'
                )}
              >
                {who(participantId, index)}
              </button>
            ))}
          </div>
          {address?.to && (
            <p className="text-xs text-muted-foreground" data-preview-to="">
              To {address.to}
              {address.cc.length > 0 && ` · CC ${address.cc.join(', ')}`}
            </p>
          )}
          <div className="flex h-[min(30rem,55dvh)] min-h-0 flex-col">
            {shown ? (
              <PreviewBody state={preview} options={PREVIEW_DEFAULTS} fill />
            ) : (
              <p className="text-muted-foreground">No recipient to show.</p>
            )}
          </div>
        </div>
        <aside className="grid content-start gap-4">
          <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1" data-campaign-recipients="">
            <dt>Recipients</dt>
            <dd className="text-right tabular-nums">{total.toLocaleString('en-US')}</dd>
            <dt className="font-medium">Reached</dt>
            <dd className="text-right font-medium tabular-nums">{reachable.toLocaleString('en-US')}</dd>
            <dt className="text-muted-foreground">Unsubscribed</dt>
            <dd className="text-right text-muted-foreground tabular-nums">{suppressed.toLocaleString('en-US')}</dd>
            <dt className="text-muted-foreground">No address</dt>
            <dd className="text-right text-muted-foreground tabular-nums">{noAddress.toLocaleString('en-US')}</dd>
          </dl>
          <ExcludedLine excluded={excluded} className="-mt-3 text-muted-foreground" />
          <div className="grid gap-1.5">
            <span className="font-medium">Attachments</span>
            {campaign.attachments.map((attachment) => (
              <span key={attachment.id} className="flex min-w-0 items-center gap-1.5" data-attachment={attachment.fileName}>
                <Paperclip className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                <span className="min-w-0 flex-1 truncate">{attachment.fileName}</span>
                <span className="text-xs text-muted-foreground">{sizeWords(attachment.size)}</span>
                <button
                  type="button"
                  aria-label={`Remove ${attachment.fileName}`}
                  onClick={() => void detach(attachment.id)}
                  className="rounded p-0.5 hover:bg-muted"
                >
                  <X className="size-3.5" aria-hidden />
                </button>
              </span>
            ))}
            <input
              ref={file}
              type="file"
              hidden
              accept=".pdf,.docx,.xlsx,.csv,.png,.jpg,.jpeg"
              onChange={(event) => {
                const chosen = event.target.files?.[0];
                if (chosen) void attach(chosen);
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="justify-self-start"
              disabled={attaching || campaign.attachments.length >= 5}
              onClick={() => file.current?.click()}
            >
              <Paperclip className="size-4" aria-hidden />
              {attaching ? 'Attaching…' : 'Attach a file…'}
            </Button>
            <span className="text-xs text-muted-foreground">PDF, Word, Excel, CSV or an image; up to 5 files.</span>
            {attachRefusal && (
              <p role="alert" className="text-xs text-destructive">
                {attachRefusal}
              </p>
            )}
          </div>
          <div className="grid gap-1">
            <Button type="button" variant="outline" size="sm" className="justify-self-start" onClick={() => void sendTest()}>
              <Send className="size-4" aria-hidden />
              Send me a test
            </Button>
            {test && (
              <span role="status" className={cn('text-xs', test.sent ? 'text-muted-foreground' : 'text-destructive')}>
                {test.text}
              </span>
            )}
          </div>
        </aside>
      </div>
    </DialogBody>
  );
}

function defaultName(templateName: string): string {
  const day = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${templateName} · ${day}`;
}

function countWords(n: number, noun: string): string {
  return `${n.toLocaleString('en-US')} ${noun}${n === 1 ? '' : 's'}`;
}

function sizeWords(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
